/**
 * Where the access token lives in the browser: in memory, and nowhere else.
 *
 * Not `localStorage` and not `sessionStorage`. Both are readable by any script
 * on the origin, so a single XSS anywhere in the app — including in a
 * dependency — hands an attacker a working credential. An ESLint rule in this
 * repo makes reaching for them an error rather than a matter of discipline.
 *
 * The cost of memory-only is that a page reload loses the access token. That
 * is fine, and is the point of the design: the *refresh* token lives in an
 * `HttpOnly` cookie the page cannot read, so a reload silently exchanges it for
 * a new access token and the user notices nothing. The credential that survives
 * a reload is the one script cannot touch.
 */

import type { components } from "./generated/schema";

/** The signed-in user, as the API describes them. */
export type User = components["schemas"]["UserPayload"];

/** What the client knows about the current session. */
export interface Session {
  accessToken: string;
  expiresAt: Date;
  /** The lineage, so a session list can mark "this device". */
  lineageId: string;
}

type Listener = (session: Session | null) => void;

/**
 * Holds the current session for the lifetime of the page.
 *
 * Observable, so the router and the UI react to a sign-out that happened
 * somewhere else — a refresh failing, a 401 on any request — without polling.
 */
export class TokenStore {
  #session: Session | null = null;
  #listeners = new Set<Listener>();

  get session(): Session | null {
    return this.#session;
  }

  get accessToken(): string | null {
    return this.#session?.accessToken ?? null;
  }

  /**
   * Whether the token is close enough to expiry to be worth refreshing.
   *
   * The lead time matters: refreshing *at* expiry means a request that was
   * already in flight arrives with a token that just died. Refreshing a little
   * early costs one extra rotation and removes that race.
   */
  isExpiringWithin(seconds: number): boolean {
    if (!this.#session) return false;
    return this.#session.expiresAt.getTime() - Date.now() < seconds * 1000;
  }

  set(session: Session | null): void {
    this.#session = session;
    for (const listener of this.#listeners) listener(session);
  }

  clear(): void {
    this.set(null);
  }

  /** Subscribe to session changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}
